import { Link as RouterLink } from "react-router-dom";
import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
} from "@mui/material";
import { formatDistanceToNowStrict } from "date-fns";
import { DataTable } from "~/components/data-table";
import { Flex } from "~/components/Flex";
import { TimerOutlined as TimerOutlinedIcon } from "@mui/icons-material";
import { Loading } from "~/components/loading";

import { SpaceScope } from "~/hooks/useScope";
import { useSpaceExceptions } from "../hooks/useSpaceExceptions";
import { EmptyExpiringExceptionsCard } from "./EmptyCards";
import { HubCard } from "~/components/hub-card";
import { ListCountChip } from "./ListCountChip";

export type ExpiringExceptionsCardProps = {
  spaceScope: SpaceScope;
  baseUrl: URL;
};

export function ExpiringExceptionsCard({
  spaceScope,
  baseUrl,
}: ExpiringExceptionsCardProps) {
  const { expiringExceptions } = useSpaceExceptions({
    scopeMrn: spaceScope.mrn,
  });
  const listExceptionGroups = expiringExceptions.data?.listExceptionGroups;

  const expiringExceptionsGroups =
    listExceptionGroups?.edges?.flatMap((eg) => eg.node ?? []) ?? [];

  const totalCount = listExceptionGroups?.totalCount || 0;
  const isEmpty = !expiringExceptions.loading && totalCount === 0;

  const viewAllUrl = new URL(baseUrl);
  viewAllUrl.searchParams.set("expires", "NEXT_WEEK");

  return (
    <HubCard
      loading={expiringExceptions.loading}
      title={
        <Flex alignItems="center" gap={1}>
          Expiring Soon
          {totalCount > 0 && <ListCountChip count={totalCount} />}
        </Flex>
      }
      icon={<TimerOutlinedIcon fontSize="small" />}
      sx={{ height: "100%", justifyContent: "space-between" }}
      data-testid="expiring-exceptions-card"
    >
      {isEmpty && <EmptyExpiringExceptionsCard />}
      {!isEmpty && (
        <Flex
          flexDirection={"column"}
          justifyContent={"space-between"}
          gap={2}
          minHeight={"100%"}
        >
          <DataTable firewatchSize={"small"}>
            <TableHead
              sx={{
                backgroundColor: "inherit",
                boxShadow: "none",
                "& th": {
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  borderBottom: "none",
                },
              }}
            >
              <TableRow>
                <TableCell>Exception ID</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Expires</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& td": {
                  fontSize: 12,
                },
              }}
            >
              {/* If the data is loading, show a loading message */}
              {expiringExceptions.loading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box sx={{ my: 10 }}>
                      <Loading what="Expiring Exceptions" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                expiringExceptionsGroups.map((eg, index) => (
                  <TableRow key={eg.exceptionId}>
                    <TableCell>{eg.exceptionId}</TableCell>
                    <TableCell>{eg.author?.name}</TableCell>
                    <TableCell>{eg.exceptions.length} Checks</TableCell>
                    <TableCell>
                      {eg.validUntil
                        ? formatDistanceToNowStrict(eg.validUntil)
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </DataTable>
          <Flex justifyContent={"center"}>
            <Button
              color="primary"
              component={RouterLink}
              to={viewAllUrl.href}
              sx={{ fontSize: 12, fontWeight: 500 }}
            >
              View All
            </Button>
          </Flex>
        </Flex>
      )}
    </HubCard>
  );
}
